import React, { useEffect, useState } from 'react';
import { Modal, Button, Message, Loader } from 'semantic-ui-react';
import axios from 'axios';

const SuccessModal = ({
    open,
    onClose,
    roles = [],  // Use provided roles as fallback
    userEmail,
    categories = [],  // Use provided categories as fallback
    requestCategory,
    isMultiTenancy = false
}) => {
    const [roleData, setRoleData] = useState({ roles, categories });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            if (!open || !userEmail) return;

            setLoading(true);
            try {
                const response = await axios.get('/api/user/roles', {
                    withCredentials: true,
                    timeout: 5000 // 5 second timeout
                });

                if (response.data.roles) {
                    setRoleData({
                        roles: response.data.roles,
                        categories: response.data.categories || []
                    });
                    setError(null);
                } else {
                    throw new Error('Invalid role data received');
                }
            } catch (err) {
                console.error('Error fetching roles:', err);
                // Fall back to provided roles if available
                if (roles.length > 0) {
                    setRoleData({ roles, categories });
                    setError(null);
                } else {
                    setError(err.response?.data?.message || 'Failed to load role information');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [open, userEmail, roles, categories]);

    const getDisplayCategory = () => {
        if (isMultiTenancy && requestCategory === 'PROD') {
            return 'PROD / POC';
        }
        return requestCategory;
    };

    const getMessage = () => {
        const hasRole = (role) => roleData.roles.includes(role);

        // Show loading message
        if (loading) {
            return (
                <Message info>
                    <Message.Header>Processing Your Request</Message.Header>
                    <p>Please wait while we finalize your submission...</p>
                </Message>
            );
        }

        // Show error message
        if (error) {
            return (
                <Message warning>
                    <Message.Header>Request Submitted Successfully</Message.Header>
                    <p>Your request has been submitted and will be processed.</p>
                    <p>You will receive further details via email.</p>
                    <p>You can track your request status in the "My Submitted Requests" tab.</p>
                </Message>
            );
        }

        // Role-specific messages
        if (hasRole('DEVOPS')) {
            return (
                <Message positive>
                    <Message.Header>Request Submitted Successfully (DevOps)</Message.Header>
                    <p>A new request has been submitted for category: {getDisplayCategory()}</p>
                    <p>As a DevOps, you can:</p>
                    <ul>
                        <li>Monitor this request in the "All Approval History" tab</li>
                        <li>View detailed approval flow and status updates</li>
                        <li>Track all system notifications</li>
                    </ul>
                </Message>
            );
        }

        if (hasRole('APPROVER')) {
            return (
                <Message positive>
                    <Message.Header>Request Submitted (Approver)</Message.Header>
                    <p>New request in category: {getDisplayCategory()}</p>
                    <ul>
                        <li>You'll be notified when final approval is needed</li>
                        <li>Check "Approver View" for pending requests</li>
                        <li>Track approvals in "Approval History"</li>
                    </ul>
                </Message>
            );
        }


        // Default message for form fillers
        return (
            <Message positive>
                <Message.Header>Request Submitted Successfully</Message.Header>
                <p>Your request for category '{getDisplayCategory()}' has been submitted.</p>
                <p><strong>Next Steps:</strong></p>
                <ul>
                    <li>You will receive a confirmation email shortly</li>
                    <li>Track your request in "My Submitted Requests"</li>
                    <li>Watch for email updates on approval status</li>
                </ul>
            </Message>
        );
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            size="small"
            closeOnDimmerClick={false}
        >
            <Modal.Header style={{
                backgroundColor: error ? '#fff7ed' : '#f0fdf4',
                color: error ? '#9a3412' : '#6F9911'
            }}>
                Request Status
            </Modal.Header>
            <Modal.Content>
                {getMessage()}
            </Modal.Content>
            <Modal.Actions>
                <Button
                    color={error ? "orange" : "green"}
                    onClick={onClose}
                    disabled={loading}
                >
                    {loading ? 'Please wait...' : 'Close'}
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default SuccessModal;